import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button } from 'reactstrap';

import isNumber from 'services/numbers/isNumber';
import formatGroupedNumber from 'services/numbers/formatGroupedNumber';
import numericStringToFloat from 'services/numbers/numericStringToFloat';
import { appendPlainTextToOfferText } from 'services/offers/offerText';
import translations from 'services/translations/translations';
import { useFeedbackSection } from 'services/feedback/feedbackSection';
import showFullTextOnHover from 'services/layout/showFullTextOnHover';
import { addMessage } from 'store/messages/actions';
import { removeNavMenuSubText, setNavMenuSubText } from 'store/navMenu/actions';
import { fetchInstallationCategories } from 'store/products/actions';
import { WRITE_PRODUCT_CONFIGURATIONS } from 'services/enums/users/permissions';
import useHasPermission from 'services/users/useHasPermission';

import NumberWithUnitInputField from 'components/layout/forms/NumberWithUnitInputField';
import LinkButton from 'components/layout/buttons/LinkButton';
import {
  contractFields,
  INSTALLATION_SECTIONS,
  sectionOf,
} from 'services/offers/installationSections';
import PrimaryButton from 'components/layout/buttons/PrimaryButton';
import { ProductImagesContext, useProductImagesShown } from './productImages';
import {
  DEFAULT_TARGET_MARGIN_PERCENT,
  priceVersionForTargetMargin,
} from './targetMargin';
import allFieldsAreFilled from './allFieldsAreFilled';
import ContractsTotal from './ContractsTotal';
import AttachConfiguration from './attachConfiguration/AttachConfiguration';
import getDefaultVersion from './getDefaultVersion';
import { getVersionWorkerDays } from './getVersionInstallationContract';
import handleArrowNavigation from './arrowNavigation';
import Header from './Header';
import IFormState from '../IFormState';
import IInstallation from '../IInstallation';
import InstallationContract from './InstallationContract';
import Installations from './installations/Installations';
import ISubproduct from '../ISubproduct';
import IDetail from '../IDetail';
import ReferencePrices from './referencePrices/ReferencePrices';
import SupplierSummary from './supplierSummary/SupplierSummary';
import RestoreDeletedRows from './RestoreDeletedRows';
import {
  IDeletedRow,
  getSubproductName,
  restoreDetail,
  restoreInstallation,
  restoreSubproduct,
} from './deletedRows';
import IVersion from '../IVersion';
import Memo from './Memo';
import SaveAsConfiguration from './SaveAsConfiguration';
import formatToApiFormat from '../formatToApiFormat';
import OfferProductImages from '../OfferProductImages';
import OtherExpenses from './installations/OtherExpenses';
import { PositionUnitContext } from './PositionUnitPrice';
import { PackageSizesContext, usePackageSizes } from './PackageSizes';
import {
  IPositionHistoryEntry,
  PositionHistoryContext,
  SummaryHistoryRows,
  loadPositionHistory,
} from './PositionHistory';
import Subproducts from './subproducts/Subproducts';
import Summary from './Summary';
import Version from './Version';
import VersionActions from './VersionActions';
import views from '../views';

import 'style/offers/CalculationWindow.css';
import BatchCalculationReviews from './batchCalculationReview/BatchCalculationReviews';
import ICalculationReview from '../ICalculationReview';

const CalculationWindow = ({
  close,
  formState,
  locked,
  offerProductId,
  offerType,
  openPreview,
  setFormState,
  setView,
  view,
  save,
}: {
  close: () => void;
  formState: IFormState;
  locked: boolean;
  // Undefined until the position is saved
  offerProductId?: number;
  // The type of the offer, e.g. preliminary-offer
  offerType?: string;
  // Opens the PDF preview, undefined when the offer has no preview yet
  openPreview?: () => void;
  setFormState: (formState: IFormState) => void;
  setView: (view: string) => void;
  view: string;
  save: (preventRedirect: boolean) => void;
}): JSX.Element | null => {
  const dispatch = useDispatch();
  const { t } = useTranslation([translations.common, translations.offers]);
  // Feedback given while the window is open is about the calculation of the position
  useFeedbackSection(
    'Positiolaskenta',
    [formState.position, formState.name].filter(Boolean).join(' ')
  );
  const [memo, setMemo] = useState<string>('');
  const [versions, setVersions] = useState<IVersion[]>([getDefaultVersion()]);
  const [activeVersionIndex, setActiveVersionIndex] = useState<number>(0);
  const [saveVersion, setSaveVersion] = useState(false);
  // The alternative is saved as a product configuration by the users who may make them
  const canSaveConfigurations = useHasPermission(WRITE_PRODUCT_CONFIGURATIONS);
  const [saveAsConfigurationOpen, setSaveAsConfigurationOpen] = useState(false);
  // Shows row numbers in front of the rows for changing their order
  const [reordering, setReordering] = useState(false);
  const [deletedRows, setDeletedRows] = useState<IDeletedRow[]>([]);
  // The margin % the rows added in the window are priced for (see targetMargin.ts)
  const [targetMarginPercent, setTargetMarginPercent] = useState<string>(
    DEFAULT_TARGET_MARGIN_PERCENT
  );
  // The sections of subcontracting and own production opened with their buttons; a section
  // with rows is always shown
  const [openedSections, setOpenedSections] = useState<string[]>([]);
  const nextDeletedRowId = useRef(0);
  const isReordering = reordering && !locked;
  const keepActiveVersion = useRef(false);
  // The number of alternatives in the form state the last time it changed
  const versionCount = useRef(0);

  // The same position on the preliminary offer and the other versions; its rows are
  // shown under the rows of the window when asked
  const [history, setHistory] = useState<IPositionHistoryEntry[]>([]);
  const [historyShown, setHistoryShown] = useState(false);
  const [productImagesShown, setProductImagesShown] = useProductImagesShown();

  useEffect(() => {
    dispatch(fetchInstallationCategories());
  }, []);

  useEffect(() => {
    setHistory([]);
    setHistoryShown(false);
    if (offerProductId === undefined) {
      return undefined;
    }

    let active = true;
    loadPositionHistory(offerProductId)
      .then((entries) => {
        if (active) {
          setHistory(entries);
        }
      })
      .catch(() => {
        if (active) {
          dispatch(
            addMessage(
              t(`${translations.calculationWindow}:History load failed`)
            )
          );
        }
      });

    return () => {
      active = false;
    };
  }, [offerProductId]);

  // The position and the alternative being calculated stay visible in the header while
  // the window is scrolled, e.g. "VS1 EK - 66 - EK · Versio 2 (pääversio)"
  const activeVersion = versions[activeVersionIndex];

  // The package sizes of the products of the material rows and their details
  const packageSizes = usePackageSizes(
    (activeVersion?.subproducts ?? []).flatMap((subproduct) => [
      Number(subproduct.product?.value) || 0,
      ...subproduct.details.map((detail) => Number(detail.product?.value) || 0),
    ])
  );
  const positionQuantity = numericStringToFloat(formState.quantity);
  const headerSubText = [
    `${formState.position} ${formState.name}`.trim(),
    // The quantity of the position with its unit, e.g. 120 m²; not on a price list row
    formState.isPriceListItem === true || !positionQuantity
      ? ''
      : `${formatGroupedNumber(positionQuantity)} ${
          formState.unit
            ? t(`${translations.common}:units:${formState.unit}`)
            : ''
        }`.trim(),
    // A price list row has no alternatives
    activeVersion === undefined || formState.isPriceListItem === true
      ? ''
      : `${t(`${translations.calculationWindow}:Version label`)} ${
          activeVersion.name
        }${
          activeVersion.isPrimary
            ? ` (${t(`${translations.calculationWindow}:primary`)})`
            : ''
        }`,
  ]
    .filter((part) => part !== '')
    .join(' · ');

  useEffect(() => {
    dispatch(setNavMenuSubText(headerSubText));
  }, [dispatch, headerSubText]);

  useEffect(
    () => () => {
      dispatch(removeNavMenuSubText());
    },
    [dispatch]
  );

  /*
   * The installation categories tell installations from other expenses. Until they are
   * loaded the installation sections would show no rows and 0 hours, so a loading text
   * is shown instead. The categories are a fixed list, so it is never empty once loaded.
   */
  const installationCategories = useSelector(
    (state: any) => state.products.installationCategories
  );
  const installationCategoriesLoaded =
    Array.isArray(installationCategories) && installationCategories.length > 0;

  // The sections of subcontracting and own production in use: opened, or with rows
  const activeInstallations = versions[activeVersionIndex]?.installations ?? [];
  const sectionsInUse = INSTALLATION_SECTIONS.filter(
    (section) =>
      openedSections.includes(section) ||
      activeInstallations.some(
        (installation) => sectionOf(installation) === section
      )
  );
  const sectionsNotInUse = INSTALLATION_SECTIONS.filter(
    (section) => !sectionsInUse.includes(section)
  );

  // A section opened without its own average hourly earnings starts with those of the
  // installation contract
  const openSection = (section: string) => {
    setOpenedSections([...openedSections, section]);
    const version = versions[activeVersionIndex];
    const fields = contractFields(section);
    if (
      version &&
      !(numericStringToFloat(version[fields.averageHourlyEarnings] ?? '') > 0)
    ) {
      setVersion({
        ...version,
        [fields.averageHourlyEarnings]: version.averageHourlyEarnings,
      });
    }
  };

  // A row of a section without anything entered: no installation, quantity or prices. A
  // loaded row without an installation has an empty choice, not null
  const isEmptyRow = (installation: IInstallation) =>
    !installation.installation?.value &&
    !installation.customInstallation &&
    !numericStringToFloat(installation.quantity ?? '') &&
    !numericStringToFloat(installation.installationPrice ?? '') &&
    !numericStringToFloat(installation.sellingPrice ?? '');

  // A section with nothing entered can be closed back to its button; its empty rows go
  const sectionCanBeClosed = (section: string) =>
    activeInstallations.every(
      (installation) =>
        sectionOf(installation) !== section || isEmptyRow(installation)
    );

  const closeSection = (section: string) => {
    setOpenedSections(
      openedSections.filter((openedSection) => openedSection !== section)
    );
    const version = versions[activeVersionIndex];
    if (
      version &&
      version.installations.some(
        (installation) => sectionOf(installation) === section
      )
    ) {
      setVersion({
        ...version,
        installations: version.installations.filter(
          (installation) => sectionOf(installation) !== section
        ),
      });
    }
  };

  const loadingInstallations = (
    <p className="calculation-loading">
      {t(`${translations.calculationWindow}:Loading installations`)}
    </p>
  );

  useEffect(() => {
    setMemo(formState.memo);
    // The margin % set for the position is kept with it
    setTargetMarginPercent(
      formState.targetMarginPercent || DEFAULT_TARGET_MARGIN_PERCENT
    );

    setVersions(
      formState.versions.length > 0
        ? [...formState.versions]
        : [getDefaultVersion()]
    );

    const getPrimaryVersionIndex = () => {
      const index = formState.versions.findIndex(
        (version) => version.isPrimary
      );

      return index === -1 ? 0 : index;
    };

    // The primary version is opened when the position is loaded. When the calculation
    // window itself updates the form state (save), the user stays on the same version.
    // The saved alternatives get their ids from the server after a save: the same
    // alternatives, so the user stays on the same one
    const sameAlternatives =
      versionCount.current > 0 &&
      versionCount.current === formState.versions.length;
    versionCount.current = formState.versions.length;

    if (keepActiveVersion.current || sameAlternatives) {
      keepActiveVersion.current = false;
      setActiveVersionIndex((current) =>
        current < formState.versions.length ? current : getPrimaryVersionIndex()
      );
    } else {
      setActiveVersionIndex(getPrimaryVersionIndex());
    }

    if (saveVersion) {
      setSaveVersion(false);
      save(true);
    }
  }, [formState]);

  const onOfferTextChanged = (newText: string) => {
    const newActiveVersion = {
      ...versions[activeVersionIndex],
      offerText: newText,
    };

    const newVersions = [
      ...versions.slice(0, activeVersionIndex),
      newActiveVersion,
      ...versions.slice(activeVersionIndex + 1),
    ];

    setVersions(newVersions);
  };

  const setStateOfForm = () => {
    if (!allFieldsAreFilled(versions)) {
      dispatch(addMessage(t(`${translations.common}:Fill all fields`)));
      return;
    }
    keepActiveVersion.current = true;
    setFormState({
      ...formState,
      memo,
      targetMarginPercent,
      versions,
    });
  };

  const ready = () => {
    setStateOfForm();

    if (allFieldsAreFilled(versions)) {
      close();
    }
  };

  const saveData = () => {
    setSaveVersion(true);
    setStateOfForm();
  };

  const setVersion = (updatedVersion: IVersion) =>
    setVersions(
      versions.map((version, index) =>
        index === activeVersionIndex ? updatedVersion : version
      )
    );

  const setInstallations = (installations: IInstallation[]) =>
    setVersion({
      ...versions[activeVersionIndex],
      installations,
    });

  const setCalculationReviews = (calculationReviews: ICalculationReview[]) =>
    setVersion({
      ...versions[activeVersionIndex],
      calculationReviews,
    });

  const setSubproducts = (subproducts: ISubproduct[]) =>
    setVersion({
      ...versions[activeVersionIndex],
      subproducts,
    });

  const setSubproductsAndInstallations = (
    subproducts: ISubproduct[],
    installations: IInstallation[],
    offerText: string
  ) => {
    const newOfferText = appendPlainTextToOfferText(
      versions[activeVersionIndex].offerText,
      offerText
    );

    subproducts.forEach((sp) => {
      sp.details.forEach((detail) => {
        detail.expenseUnit =
          detail.expenseUnit === '' ||
          detail.expenseUnit === detail.quantityUnit
            ? detail.expenseUnit
            : detail.quantityUnit;
      });
    });

    setVersion({
      ...versions[activeVersionIndex],
      installations,
      subproducts,
      offerText: newOfferText,
    });
  };

  // Deleted rows can be restored while the calculation window is open, also after saving
  const getDeletedRowId = () => {
    nextDeletedRowId.current += 1;
    return nextDeletedRowId.current;
  };

  const keepDeletedSubproduct = (subproduct: ISubproduct, index: number) =>
    setDeletedRows((rows) => [
      ...rows,
      {
        id: getDeletedRowId(),
        index,
        kind: 'subproduct',
        row: subproduct,
        versionIndex: activeVersionIndex,
      },
    ]);

  const keepDeletedInstallation = (
    installation: IInstallation,
    index: number
  ) =>
    setDeletedRows((rows) => [
      ...rows,
      {
        id: getDeletedRowId(),
        index,
        kind: 'installation',
        row: installation,
        versionIndex: activeVersionIndex,
      },
    ]);

  const keepDeletedDetail = (
    subproduct: ISubproduct,
    detail: IDetail,
    index: number
  ) =>
    setDeletedRows((rows) => [
      ...rows,
      {
        id: getDeletedRowId(),
        index,
        kind: 'detail',
        parentKey: subproduct.key,
        parentName: getSubproductName(subproduct),
        row: detail,
        versionIndex: activeVersionIndex,
      },
    ]);

  const restoreDeletedRow = (deletedRow: IDeletedRow) => {
    const version = versions[activeVersionIndex];

    if (deletedRow.kind === 'subproduct') {
      setSubproducts(restoreSubproduct(version.subproducts, deletedRow));
    } else if (deletedRow.kind === 'installation') {
      setInstallations(restoreInstallation(version.installations, deletedRow));
    } else {
      const subproducts = restoreDetail(version.subproducts, deletedRow);
      if (subproducts === null) {
        dispatch(
          addMessage(
            t(
              `${translations.calculationWindow}:Restore the material row first`
            )
          )
        );
        return;
      }

      setSubproducts(subproducts);
    }

    setDeletedRows((rows) => rows.filter((row) => row.id !== deletedRow.id));
  };

  // A new target margin prices again the rows following it in all alternatives
  const handleTargetMarginChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = event.target;
    setTargetMarginPercent(value);

    if (isNumber(value) && numericStringToFloat(value) < 100) {
      setVersions(
        versions.map((version) => priceVersionForTargetMargin(version, value))
      );
    }
  };

  const deletedRowsOfVersion = deletedRows.filter(
    (deletedRow) => deletedRow.versionIndex === activeVersionIndex
  );

  if (view === views.attachConfiguration) {
    return (
      <div className="calculation-window">
        <AttachConfiguration
          cancel={() => setView(views.calculationWindow)}
          formState={formState}
          locked={locked}
          installations={versions[activeVersionIndex].installations}
          setSubproductsAndInstallations={setSubproductsAndInstallations}
          subproducts={versions[activeVersionIndex].subproducts}
        />
      </div>
    );
  }

  return (
    // Fields whose text does not fit show the whole text when the pointer is on them
    // or when they get the focus
    <div
      className="calculation-window"
      onFocus={showFullTextOnHover}
      // The fields of the rows are moved between with the arrow keys
      onKeyDown={handleArrowNavigation}
      onMouseOver={showFullTextOnHover}
      role="group"
    >
      <Header
        activeVersionIndex={activeVersionIndex}
        formState={formState}
        locked={locked}
        offerProductId={offerProductId}
        offerType={offerType}
        setActiveVersionIndex={setActiveVersionIndex}
        setVersions={setVersions}
        setView={setView}
        versions={versions}
      />
      {locked ? null : (
        <div className="row-order-toolbar">
          <div className="target-margin">
            <NumberWithUnitInputField
              id="target-margin"
              label={t(`${translations.calculationWindow}:Target margin`)}
              maxIntegerDigits={2}
              name="targetMarginPercent"
              onChange={handleTargetMarginChange}
              unit="%"
              value={targetMarginPercent}
            />
          </div>
          <RestoreDeletedRows
            deletedRows={deletedRowsOfVersion}
            restore={restoreDeletedRow}
          />
          {reordering ? (
            <span className="row-order-info">
              {t(`${translations.calculationWindow}:Row order info`)}
            </span>
          ) : null}
          <PrimaryButton
            className={reordering ? 'row-order-active' : undefined}
            onClick={() => setReordering(!reordering)}
            size="sm"
          >
            {reordering
              ? t(`${translations.calculationWindow}:Row order done`)
              : t(`${translations.calculationWindow}:Change row order`)}
          </PrimaryButton>
        </div>
      )}
      {/* The icons of the product images after the products of the rows */}
      <div className="position-history-toolbar">
        <Button
          active={productImagesShown}
          className="position-history-toggle"
          onClick={() => setProductImagesShown(!productImagesShown)}
          size="sm"
          title={t(`${translations.calculationWindow}:productImages.Title`)}
        >
          {productImagesShown
            ? t(`${translations.calculationWindow}:productImages.Hide`)
            : t(`${translations.calculationWindow}:productImages.Show`)}
        </Button>
      </div>
      {history.length === 0 ? null : (
        <div className="position-history-toolbar">
          <Button
            active={historyShown}
            className="position-history-toggle"
            onClick={() => setHistoryShown(!historyShown)}
            size="sm"
            title={t(`${translations.calculationWindow}:History title`)}
          >
            {historyShown
              ? t(`${translations.calculationWindow}:History hide`)
              : t(`${translations.calculationWindow}:History show`)}
          </Button>
        </div>
      )}
      {/* The rows show their selling price for one unit of the position (e.g. m2) */}
      <PackageSizesContext.Provider value={packageSizes}>
        <PositionHistoryContext.Provider
          value={
            historyShown
              ? { entries: history, versionName: activeVersion?.name ?? '' }
              : null
          }
        >
          <PositionUnitContext.Provider value={formState.unit ?? ''}>
            <ProductImagesContext.Provider value={productImagesShown}>
              <Subproducts
                locked={locked}
                onRemoveDetail={keepDeletedDetail}
                onRemoveSubproduct={keepDeletedSubproduct}
                quantity={numericStringToFloat(formState.quantity)}
                reordering={isReordering}
                setSubproducts={setSubproducts}
                subproducts={versions[activeVersionIndex].subproducts}
                targetMarginPercent={targetMarginPercent}
              />
            </ProductImagesContext.Provider>
            {installationCategoriesLoaded ? (
              <>
                <Installations
                  locked={locked}
                  installations={versions[activeVersionIndex].installations}
                  onRemoveInstallation={keepDeletedInstallation}
                  quantity={numericStringToFloat(formState.quantity)}
                  reordering={isReordering}
                  setInstallations={setInstallations}
                  targetMarginPercent={targetMarginPercent}
                />
                {/* Subcontracting and own production, when in use */}
                {sectionsInUse.map((section) => (
                  <div
                    className={`calculation-section calculation-section-${section}`}
                    key={section}
                  >
                    {locked || !sectionCanBeClosed(section) ? null : (
                      <LinkButton
                        className="calculation-section-close"
                        onClick={() => closeSection(section)}
                      >
                        {`× ${t(
                          `${translations.calculationWindow}:sections.Close section`
                        )}`}
                      </LinkButton>
                    )}
                    <Installations
                      locked={locked}
                      installations={versions[activeVersionIndex].installations}
                      onRemoveInstallation={keepDeletedInstallation}
                      quantity={numericStringToFloat(formState.quantity)}
                      reordering={isReordering}
                      section={section}
                      setInstallations={setInstallations}
                      targetMarginPercent={targetMarginPercent}
                      title={t(
                        `${translations.calculationWindow}:sections.${section}`
                      )}
                    />
                  </div>
                ))}
                {locked || sectionsNotInUse.length === 0 ? null : (
                  <div className="calculation-add-sections">
                    {sectionsNotInUse.map((section) => (
                      <LinkButton
                        key={section}
                        onClick={() => openSection(section)}
                      >
                        {`+ ${t(
                          `${translations.calculationWindow}:sections.Add ${section}`
                        )}`}
                      </LinkButton>
                    ))}
                  </div>
                )}
                <OtherExpenses
                  locked={locked}
                  installations={versions[activeVersionIndex].installations}
                  onRemoveInstallation={keepDeletedInstallation}
                  quantity={numericStringToFloat(formState.quantity)}
                  reordering={isReordering}
                  setInstallations={setInstallations}
                  targetMarginPercent={targetMarginPercent}
                  workerDays={getVersionWorkerDays(
                    versions[activeVersionIndex],
                    numericStringToFloat(formState.quantity),
                    installationCategories
                  )}
                />
              </>
            ) : (
              loadingInstallations
            )}
          </PositionUnitContext.Provider>
          <hr />
          <Summary
            installations={versions[activeVersionIndex].installations}
            productUnit={formState.unit}
            quantity={numericStringToFloat(formState.quantity)}
            subproducts={versions[activeVersionIndex].subproducts}
          />
          {/* The same summary on the preliminary offer and the other versions */}
          <SummaryHistoryRows />
        </PositionHistoryContext.Provider>
      </PackageSizesContext.Provider>
      <hr />
      {installationCategoriesLoaded ? (
        <>
          <InstallationContract
            locked={locked}
            productUnit={formState.unit}
            quantity={numericStringToFloat(formState.quantity)}
            setVersion={setVersion}
            version={versions[activeVersionIndex]}
          />
          {/* The summary rows of subcontracting and own production, and their total */}
          {sectionsInUse.map((section) => (
            <div
              className={`calculation-section calculation-section-${section}`}
              key={section}
            >
              <InstallationContract
                locked={locked}
                productUnit={formState.unit}
                quantity={numericStringToFloat(formState.quantity)}
                section={section}
                setVersion={setVersion}
                title={t(
                  `${translations.calculationWindow}:sections.${section}`
                )}
                version={versions[activeVersionIndex]}
              />
            </div>
          ))}
          {sectionsInUse.length > 0 ? (
            <ContractsTotal
              quantity={numericStringToFloat(formState.quantity)}
              sections={sectionsInUse}
              version={versions[activeVersionIndex]}
            />
          ) : null}
          <hr />
          <BatchCalculationReviews
            locked={locked}
            productUnit={formState.unit}
            quantity={numericStringToFloat(formState.quantity)}
            setCalculationReviews={setCalculationReviews}
            version={versions[activeVersionIndex]}
            calculationReviews={
              versions[activeVersionIndex].calculationReviews ?? []
            }
          />
        </>
      ) : (
        loadingInstallations
      )}
      <hr />
      {/* A price list row has only its memo: no alternatives, offer text or images */}
      {formState.isPriceListItem === true ? (
        <div className="version-details">
          <div className="version-details-internal">
            <p className="version-details-title">
              {t(`${translations.calculationWindow}:Internal`)}
              <span className="version-details-tag internal">
                {t(`${translations.calculationWindow}:Not printed`)}
              </span>
            </p>
            <div className="version-details-box">
              <Memo memo={memo} onTextInputChanged={setMemo} locked={locked} />
            </div>
          </div>
        </div>
      ) : (
        <>
          <h2>{t(`${translations.calculationWindow}:Version details`)}</h2>
          {/* Left: internal. Right: what is printed in the offer, in the order of the PDF. */}
          <div className="version-details">
            <div className="version-details-internal">
              <p className="version-details-title">
                {t(`${translations.calculationWindow}:Internal`)}
                <span className="version-details-tag internal">
                  {t(`${translations.calculationWindow}:Not printed`)}
                </span>
              </p>
              <div className="version-details-box">
                <Memo
                  memo={memo}
                  onTextInputChanged={setMemo}
                  locked={locked}
                />
              </div>
              <VersionActions
                activeVersionIndex={activeVersionIndex}
                locked={locked}
                setActiveVersionIndex={setActiveVersionIndex}
                setVersions={setVersions}
                versions={versions}
              />
              <ReferencePrices
                name={formState.name}
                offerProductId={offerProductId}
                offerType={offerType}
                unit={formState.unit}
              />
              <SupplierSummary
                installations={versions[activeVersionIndex].installations}
                offerProductId={offerProductId}
                quantity={numericStringToFloat(formState.quantity)}
                subproducts={versions[activeVersionIndex].subproducts}
              />
            </div>
            <div className="version-details-printed">
              <p className="version-details-title">
                {t(`${translations.calculationWindow}:Printed in offer`)}
                <span className="version-details-tag">PDF</span>
              </p>
              <Version
                activeVersionIndex={activeVersionIndex}
                locked={locked}
                offerText={versions[activeVersionIndex].offerText}
                onOfferTextChanged={onOfferTextChanged}
                setActiveVersionIndex={setActiveVersionIndex}
                setVersions={setVersions}
                versions={versions}
              />
              {/* Images of the active alternative, printed after its text in the offer PDF */}
              {offerProductId !== undefined &&
              versions[activeVersionIndex].id > 0 ? (
                <OfferProductImages
                  key={versions[activeVersionIndex].id}
                  locked={locked}
                  offerProductId={offerProductId}
                  versionId={versions[activeVersionIndex].id}
                />
              ) : (
                <p className="offer-product-images-info">
                  {t(
                    `${translations.offers}:Save alternative before adding images`
                  )}
                </p>
              )}
            </div>
          </div>
        </>
      )}
      <div className="calculation-window-action-bar">
        <span>
          <PrimaryButton
            className="button-one-line outline-button"
            onClick={close}
          >
            {t(`${translations.common}:Cancel`).toLocaleUpperCase()}
          </PrimaryButton>
        </span>
        <span className="calculation-window-action-bar-right">
          {/* The calculation as a product configuration, also from a locked offer */}
          {canSaveConfigurations && formState.isPriceListItem !== true ? (
            <PrimaryButton
              className="button-one-line outline-button"
              onClick={() => setSaveAsConfigurationOpen(true)}
            >
              {t(
                `${translations.calculationWindow}:saveAsConfiguration.Button`
              ).toLocaleUpperCase()}
            </PrimaryButton>
          ) : null}
          {openPreview === undefined ? null : (
            <PrimaryButton
              className="button-one-line outline-button"
              onClick={openPreview}
            >
              {t(`${translations.offers}:pdfPreview.Open`).toLocaleUpperCase()}
            </PrimaryButton>
          )}
          {locked ? null : (
            <>
              <PrimaryButton className="button-one-line" onClick={saveData}>
                {t(`${translations.common}:Save`).toLocaleUpperCase()}
              </PrimaryButton>
              <PrimaryButton className="button-one-line" onClick={ready}>
                {t(`${translations.common}:Ready`).toLocaleUpperCase()}
              </PrimaryButton>
            </>
          )}
        </span>
      </div>
      {/* Save always at hand in the lower right corner of the page, so a long calculation
          is saved without scrolling to the buttons at its end */}
      {locked ? null : (
        <PrimaryButton
          className="button-one-line calculation-window-floating-save"
          onClick={saveData}
        >
          {t(`${translations.common}:Save`).toLocaleUpperCase()}
        </PrimaryButton>
      )}
      <SaveAsConfiguration
        defaultName={`${formState.position} ${formState.name}`.trim()}
        isOpen={saveAsConfigurationOpen}
        toggle={() => setSaveAsConfigurationOpen(!saveAsConfigurationOpen)}
        version={() =>
          formatToApiFormat({
            ...formState,
            versions: [versions[activeVersionIndex]],
          }).versions[0]
        }
      />
    </div>
  );
};

export default CalculationWindow;
